import { useEffect, useMemo, useState } from 'react';
import { NewsletterAdminAPI } from '../api';
import './home.css';

export default function AdminNewsletter() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ pending: 0, confirmed: 0, unsubscribed: 0, total: 0 });

  const load = async (opts = {}) => {
    setLoading(true);
    try {
      const params = { q, status, limit, offset, ...opts };
      const data = await NewsletterAdminAPI.list(params);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setLimit(data.limit || 50);
      setOffset(data.offset || 0);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load({ offset: 0 });
    // also refresh stats
    (async () => {
      try { setStats(await NewsletterAdminAPI.stats()); } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const onSearch = (e) => {
    e.preventDefault();
    load({ offset: 0 });
  };

  // Derived pagination info
  const pageInfo = useMemo(() => {
    const page = Math.floor(offset / limit) + 1;
    const pages = Math.max(Math.ceil(total / limit), 1);
    return { page, pages };
  }, [offset, limit, total]);

  // Change page helper
  const changePage = (delta) => {
    const newOffset = Math.max(0, Math.min(offset + delta * limit, Math.max(total - limit, 0)));
    setOffset(newOffset);
    load({ offset: newOffset });
  };

  // CSV export
  const doExport = async () => {
    try {
      const blob = await NewsletterAdminAPI.exportCsv({ q, status });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'newsletter-subscribers.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      alert('CSV-Export fehlgeschlagen');
    }
  };

  // Resend confirmation for pending
  const resend = async (email) => {
    if (!email) return;
    const ok = confirm(`Bestätigung erneut an ${email} senden?`);
    if (!ok) return;
    try {
      await NewsletterAdminAPI.resendConfirmation(email);
      alert('Bestätigung erneut gesendet.');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      alert('Konnte nicht senden.');
    }
  };

  return (
    <div className="home-page">
      {/* Hero Header */}
      <section style={{ width:'100vw', margin:0, padding:0, position:'relative', left:'-120px' }}>
        <h1 className="hero-title" style={{ textAlign: 'center', margin:'0 0 8px' }}>Newsletter</h1>
        <p className="hero-desc" style={{ margin:'0 auto 24px', textAlign: 'center', maxWidth:760 }}>Verwalte Abonnenten, filtere nach Status und exportiere CSV.</p>
      </section>

      {/* Content */}
      <section style={{ width: '100%', maxWidth: 1100, margin: '0 auto', padding: '0 16px 40px 16px', color: '#fff' }}>
        {/* Stats chips */}
        <div style={{ display: 'flex', gap: 12, margin: '8px 0 16px 0', flexWrap: 'wrap' }}>
          <StatusChip label="Ausstehend" value={stats.pending} color="#d97706" />
          <StatusChip label="Bestätigt" value={stats.confirmed} color="#059669" />
          <StatusChip label="Abgemeldet" value={stats.unsubscribed} color="#dc2626" />
          <StatusChip label="Gesamt" value={stats.total} color="#334155" />
        </div>

        {/* Filter & actions card */}
        <div className="highlight-card" style={{ gap: 18, padding: 24, width: '100%', marginBottom: 16 }}>
          <form onSubmit={onSearch} style={{ display: 'grid', gridTemplateColumns: '1fr 220px auto auto', gap: 12, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Suchen (E-Mail, Name, Plattform, Quelle)"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="hero-btn-secondary"
              style={{ padding: '12px 14px', fontSize: '1rem' }}
            />
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="hero-btn-secondary" style={{ padding: '12px 14px', fontSize: '1rem' }}>
              <option value="">Alle Status</option>
              <option value="pending">Ausstehend</option>
              <option value="confirmed">Bestätigt</option>
              <option value="unsubscribed">Abgemeldet</option>
            </select>
            <button type="submit" disabled={loading} className="hero-btn-primary">{loading ? 'Lade…' : 'Suchen'}</button>
            <button type="button" onClick={doExport} className="hero-btn-secondary">CSV exportieren</button>
          </form>
        </div>

        {/* Table */}
        <div style={{ border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, background: 'rgba(255,255,255,0.03)' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
                  <th style={th}>E-Mail</th>
                  <th style={th}>Vorname</th>
                  <th style={th}>Twitch</th>
                  <th style={th}>TikTok</th>
                  <th style={th}>YouTube</th>
                  <th style={th}>Quelle</th>
                  <th style={th}>Status</th>
                  <th style={th}>Bestätigt am</th>
                  <th style={th}>Abgemeldet am</th>
                  <th style={th}>Angelegt</th>
                  <th style={th}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={11} style={{ padding: 16 }}>Laden…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={11} style={{ padding: 16, opacity: .8 }}>Keine Einträge</td></tr>
                ) : items.map((it) => (
                  <tr key={it.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <td style={td}>{it.email}</td>
                    <td style={td}>{it.firstName || ''}</td>
                    <td style={td}>{it.twitch || ''}</td>
                    <td style={td}>{it.tiktok || ''}</td>
                    <td style={td}>{it.youtube || ''}</td>
                    <td style={td}>{it.source || ''}</td>
                    <td style={td}>{it.status}</td>
                    <td style={td}>{it.confirmedAt ? new Date(it.confirmedAt).toLocaleString() : ''}</td>
                    <td style={td}>{it.unsubscribedAt ? new Date(it.unsubscribedAt).toLocaleString() : ''}</td>
                    <td style={td}>{it.createdAt ? new Date(it.createdAt).toLocaleString() : ''}</td>
                    <td style={td}>
                      {it.status === 'pending' && (
                        <button type="button" onClick={() => resend(it.email)} className="hero-btn-secondary" style={{ padding: '6px 10px', fontSize: 13 }}>Bestätigung erneut senden</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
          <div>Gesamt: {total}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="button" className="hero-btn-secondary" disabled={offset <= 0} onClick={() => changePage(-1)}>« Zurück</button>
            <span>Seite {pageInfo.page} / {pageInfo.pages}</span>
            <button type="button" className="hero-btn-secondary" disabled={offset + limit >= total} onClick={() => changePage(1)}>Weiter »</button>
          </div>
        </div>
      </section>
    </div>
  );
}

const th = { textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #eee', fontWeight: 600 };
const td = { textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid #f1f1f1' };

function StatusChip({ label, value, color = '#334155' }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      border: `1px solid ${color}`, color,
      borderRadius: 999, padding: '4px 10px', fontSize: 13,
    }}>
      <strong>{label}:</strong> <span>{value}</span>
    </span>
  );
}
